/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { useDateTime } from '../utils';
import { usePageContext } from '../Page/Page';

type Props = {
  children: ReactNode;
  heading: string;
  place?: string;
  time?: string;
  logo?: string;
  headingImage?: string;
};

const Styles = labelStyles('RegistrationForm', {
  root: css({
    padding: '40px 20px',
    backgroundColor: Colors.earth,

    [MEDIA_MIN_WIDTH.small]: {
      padding: '40px 30px',
    },
  }),
  maxContainer: css({
    maxWidth: '1210px',
    margin: '0 auto',
  }),
  content: css({
    backgroundColor: Colors.white,
    maxWidth: '1170px',
    [MEDIA_MIN_WIDTH.large]: {
      display: 'flex',
    },
    '@media(min-width: 1210px)': {
      margin: 'auto',
    },
  }),
  headingOuter: (backgroundImage?: string) =>
    css({
      paddingBottom: '21%',
      backgroundImage: `url("${backgroundImage?.replace(
        'upload/',
        'upload/a_90/',
      )}")`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'bottom',
      borderBottom: `1px solid ${Colors.earth}`,
      [MEDIA_MIN_WIDTH.small]: {
        paddingBottom: '0',
        paddingRight: '45%',
        backgroundImage: `url("${backgroundImage}")`,
        backgroundSize: '45% 100%',
        backgroundPosition: 'right top',
      },
      [MEDIA_MIN_WIDTH.large]: {
        width: '32%',
        paddingRight: '0',
        paddingBottom: '60%',
        backgroundSize: '100% 60%',
        backgroundPosition: 'bottom',
        borderBottom: 'none',
        borderRight: `1px solid ${Colors.earth}`,
      },
    }),

  headingInner: css({
    height: '100%',
    backgroundColor: Colors.white,
    padding: '23px 22px',
    borderBottom: `1px solid ${Colors.earth}`,
    [MEDIA_MIN_WIDTH.small]: {
      padding: '48px 28px',

      borderBottom: 'none',
      borderRight: `1px solid ${Colors.earth}`,
    },
    [MEDIA_MIN_WIDTH.large]: {
      borderRight: 'none',
      borderBottom: `1px solid ${Colors.earth}`,
    },
  }),
  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '61px',
    lineHeight: '61px',
    letterSpacing: '0.61px',
    fontWeight: 500,
  }),
  details: css({
    marginTop: '30px',
    marginBottom: '20px',
    fontFamily: 'Chivo Mono',
    lineHeight: '30px',
    letterSpacing: '2.44px',
    [MEDIA_MIN_WIDTH.small]: {
      marginBottom: '111px',
    },
  }),
  form: css({
    margin: '0 auto',
    padding: '24px 11px 14px 11px',
    h3: { color: Colors.earth },

    'div[data-size]': {
      backgroundColor: `${Colors.white} !important`,
    },
    'button[type="submit"]:not(:hover)': {
      backgroundColor: `${Colors.earth} !important`,
    },
    'button[type="submit"]:hover': {
      backgroundColor: '#DDFF76 !important',
      color: `${Colors.earth} !important`,
      boxShadow: 'none !important',
    },
    'span[class$="checkboxLabel"], span[class$="LabeledBooleanInput"]': {
      color: `${Colors.neutral700} !important`,
    },
    [MEDIA_MIN_WIDTH.small]: {
      padding: '40px 114px 56px 114px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      padding: '40px 158px 56px 158px',
    },
  }),
  imageContainer: css({
    margin: '28px 20px 0px 20px',
    [MEDIA_MIN_WIDTH.small]: { margin: '38px 20px 28px 20px' },
    [MEDIA_MIN_WIDTH.large]: { margin: '88px 20px 78px 20px' },
  }),
  image: css({ width: '100%' }),
});
export function RegistrationForm({
  children,
  heading,
  headingImage,
  logo,
  place,
  time,
}: Props) {
  const { showLocalTime } = usePageContext();
  const { addHours, compressedTime, day, amPM, timeZoneAbbreviation } =
    useDateTime(time);

  return (
    <div css={Styles.root}>
      <div css={Styles.maxContainer}>
        <div css={Styles.content}>
          <div css={Styles.headingOuter(headingImage)}>
            <div css={Styles.headingInner}>
              <h2 css={Styles.heading}>{heading}</h2>
              <div css={Styles.details}>
                {day && <p>{day}</p>}
                {time && (
                  <p>
                    {showLocalTime
                      ? `${compressedTime}${amPM} ${timeZoneAbbreviation}`
                      : `${compressedTime}${amPM} ${timeZoneAbbreviation} / ${
                          addHours(3).time
                        }${addHours(3).amPM} EDT`}
                  </p>
                )}
                {place && <p>{place}</p>}
              </div>
            </div>
          </div>
          <div css={Styles.form}>{children}</div>
        </div>
        {logo && (
          <div css={Styles.imageContainer}>
            <img alt="ZapConnect logo" css={Styles.image} src={logo} />
          </div>
        )}
      </div>
    </div>
  );
}
